import { GetServerSideProps } from "next";
import { getBookChapterExerciseData, getBooksData, getBooksList } from "@/helpers/api/service/books";
import RelatedLinks from "@/components/Calculator/RelatedLinks";
import BookListV2 from "@/components/Book/BookV2";
import { IBookChapter, IBookChapterExercise, IBookList, IBookPageData } from "./interface";
import LinkWrapper from "@/components/LinkWrapper";
import { useRouter } from "next/router";
import Table from "@/components/Table";
import { slugify } from "@/utils";
import SimilarBlock from "@/components/Similar/SimilarBlock";
import { MathJaxContext } from "better-react-mathjax";
import { getLanguageFromLocale, getLanguageFromNonNativeLocale } from "@/utils/language";
import FaDownload from "@/components/SVG/Fontawesome/FaDownload";
import FaFilePdf from "@/components/SVG/Fontawesome/FaFilePdf";
import MetaSeo from "@/components/Head/Meta";

const Books = (props: { data: IBookPageData; breadcrumbs: { items: { text: string; url?: string; }[] } }) => {
  const router = useRouter();

  const MathJaxConfig = {
    loader: { load: ["input/asciimath", "output/chtml"] }
  };

  return (
    <>
      <MetaSeo title={props.data.title} description={props.data.description} />
      <div className="body-content flex flex-wrap lg:flex-nowrap gap-x-12 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-base">
        <div className="grow">
          <section>
            <h1>{props.data.title}</h1>
          </section>
          {
            props.data.viewType === "BOOK_LIST" && <>
              {
                router.query.path && <>
                  <section>
                    <h3>Filter by Subject:</h3>
                    <ol className="list-inside pl-0">
                      {
                        (props.data.list as IBookList[]).map((item, i) => {
                          return <li className="pl-0" key={i.toString()}>
                            <LinkWrapper className="link" href={`#${slugify(item.title)}`}>{item.title}</LinkWrapper>
                          </li>
                        })
                      }
                    </ol>
                  </section>
                  <hr className="my-0" />
                </>
              }
              {
                (props.data.list as IBookList[]).map((item, i) => {
                  const bookLevelData = props.data.list[i] as IBookList;
                  return (
                    <section key={i.toString()}>
                      <h2 id={`${slugify(item.title)}`}>{item.title}</h2>
                      <BookListV2 viewAllHref={bookLevelData.cta_url} items={bookLevelData.list.map((x) => {
                        const medium = getLanguageFromNonNativeLocale(x.medium, true) || getLanguageFromLocale(x.medium);
                        return {
                          subtitle: x.title,
                          url: `/books/${x.web_url}`,
                          alt: x.title,
                          thumbnail: x.image_url,
                          labels: [
                            `Class ${x.class}`,
                            x.subject,
                            medium ? `${medium} Medium` : "",
                          ].filter(Boolean),
                        }
                      })} />
                    </section>
                  )
                })
              }
            </>
          }
          {
            props.data.viewType === "CHAPTER" && <>
              <section>
                <h3>Table Of Contents:</h3>
                <ol className="list-inside pl-0">
                  {
                    (props.data.list as IBookChapter[]).map((item, i) => {
                      return <li className="pl-0" key={i.toString()}>
                        <LinkWrapper className="link" href={`#chapter-${i + 1}-${slugify(item.name)}`}>{item.name}</LinkWrapper>
                      </li>
                    })
                  }
                </ol>
              </section>
              <section>
                <Table className="border-2 border-gray-200 table-fixed font-normal !mt-8" rows={[
                  [{ text: "Class", className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapter[])[0].student_class, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: "Subject", className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapter[])[0].subject, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: "Number Of Chapters", className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapter[]).length, className: "border-2 border-gray-200 px-4 py-2" }]
                ]} />
              </section>
              <section>
                <ol className="list-none pl-0">
                  {
                    (props.data.list as IBookChapter[]).map((item, i) => {
                      return <li className="pl-0" key={i.toString()}>
                        <h3 id={`chapter-${i + 1}-${slugify(item.name)}`}>Chapter {i + 1}: {item.name}</h3>
                        {/* {item.description && <p>{item.description}</p>} */}
                        {/* <strong>List of Exercises:</strong> */}
                        <ol>
                          {
                            item.list.map((x, j) => {
                              return <li className="pl-0" key={j.toString()}>
                                <LinkWrapper className="link" href={`/books/${x.web_url}`}>{x.name}</LinkWrapper>
                              </li>
                            })
                          }
                        </ol>
                        {
                          item.pdf_meta_info?.pdf_url && <LinkWrapper className="link flex items-center gap-2 text-xs mt-4" href={item.pdf_meta_info.pdf_url} target="_blank">
                            <FaFilePdf className="text-sm text-primary" />
                            Download {item.name} {props.breadcrumbs?.items?.[props.breadcrumbs?.items?.length - 1]?.text} PDF
                            <FaDownload className="w-4 h-4 text-primary" />
                          </LinkWrapper>
                        }
                      </li>
                    })
                  }
                </ol>
              </section>
            </>
          }
          {
            props.data.viewType === "EXERCISE" && <>
              <section>
                <Table className="border-2 border-gray-200 table-fixed font-normal !mt-8" rows={[
                  [{ text: `Class ${(props.data.list as IBookChapterExercise[])[0].class}`, className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapterExercise[])[0].subject, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: "Chapter", className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapterExercise[])[0].chapter, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: (props.data.list as IBookChapterExercise[])[0].book, className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapterExercise[])[0].exercise, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: "Number Of Questions", className: "border-2 border-gray-200 px-4 py-2" }, { text: (props.data.list as IBookChapterExercise[]).length, className: "border-2 border-gray-200 px-4 py-2" }],
                  [{ text: "Academic Year", className: "border-2 border-gray-200 px-4 py-2" }, { text: "2023-24", className: "border-2 border-gray-200 px-4 py-2" }]
                ]} />
              </section>
              <section>
                <ol className="list-none pl-0">
                  <MathJaxContext config={MathJaxConfig}>
                    {
                      (props.data.list as IBookChapterExercise[]).map((item, i) => {
                        return <SimilarBlock key={i.toString()} data={{
                          ocr_text: item.ocr_text,
                          question_id: item.question_id,
                          subject: item.subject?.toLowerCase(),
                          url: item.web_url?.split("-").slice(0, -1).join("-"),
                          duration: +item.duration,
                        }} similarMathJax={item.ocr_text} target="_blank" />
                      })
                    }
                  </MathJaxContext>
                </ol>
              </section>
            </>
          }
          {
            props.data.description && <section>
              <p>{props.data.description}</p>
            </section>
          }
        </div>
        <div className="basis-full lg:basis-1/3 shrink-0">
          {
            props.data.relatedLinks?.map((relatedLinks, i) => {
              return <section key={i.toString()}>
                <RelatedLinks {...relatedLinks} />
              </section>
            })
          }
        </div>
      </div>
    </>
  );
}


export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const path = ctx.query.path ? Array.isArray(ctx.query.path) ? ctx.query.path : [ctx.query.path] : undefined;
  let data: IBookPageData | null = null;

  if (!path) {
    const res = await getBooksList({});
    data = res.data;
    (res.data.list as IBookList[]).forEach(x => {
      x.cta_url = x.cta_url ? `/books/${x.cta_url}` : "";
    })
    data.viewType = "BOOK_LIST";
    data.relatedLinks = [{
      title: "Filter By Class",
      items: (data.list as IBookList[]).map((x) => ({ text: x.title, url: x.cta_url })),
    }];
  } else if (path.length > 2 || path.join("/").includes(".")) {
    return {
      notFound: true,
    }
  } else if (path.length > 1) {
    const fullPath = path.join("/");
    const res = await getBookChapterExerciseData(fullPath);
    data = res.data;
    const exercise = data.list.find((x: any) => x.web_url === fullPath) || data.list.find((x: any) => !x.web_url?.includes("/"));
    if (!exercise) {
      return {
        redirect: {
          destination: `/books/${path[0]}`,
          permanent: true,
          basePath: false,
        },
      };
    }
    data.relatedLinks = [{
      title: `All Exercises from ${exercise.list[0].book} Class ${exercise.list[0].class} ${exercise.subject} ${exercise.list[0].chapter}`,
      items: data.list.map((x: any) => ({ text: x.name, url: `/books/${x.web_url}` }))
    }]
    data.list = exercise.list;
    data.list[0].exercise = exercise.name;
    data.list[0].subject = exercise.subject;
    data.viewType = "EXERCISE";
  } else {
    const pathSplits = path[0]?.split("-");
    const studentClass = pathSplits[1];
    if (pathSplits[2] === "all" && pathSplits[3] === "books") {
      const res = await getBooksList({ studentClass });
      data = res.data;
      data.viewType = "BOOK_LIST";
      const groupedData = (data.list as IBookList[]).reduce((acc: any, curr: any) => {
        acc[curr.subject] = acc[curr.subject] || [];
        acc[curr.subject].push(curr);
        return acc;
      }, {});
      data.list = Object.keys(groupedData).map((key) => ({
        title: key,
        list: groupedData[key],
      }));
      const { data: relatedData } = await getBooksList({});
      data.relatedLinks = [{
        title: "Books for other classes",
        items: (relatedData.list as IBookList[]).map((x) => ({ text: x.title, url: `/books/${x.cta_url}` })),
      }];
    } else {
      let res = await getBooksData(path[0]);
      data = res.data;
      data.viewType = "CHAPTER";
      if (data.list?.length) {
        const { data: relatedData } = await getBooksList({ studentClass, subject: data.list[0]?.subject });
        const startIndex = Math.random() * (relatedData.list.length - 6) | 0;
        data.relatedLinks = [{
          title: `Other Books for Class ${relatedData.list[0].class} ${relatedData.list[0].subject}`,
          items: relatedData.list.slice(startIndex, startIndex + 6).map((x: any) => ({ text: x.title, url: `/books/${x.web_url}` }))
        }];
      } else {
        return {
          redirect: {
            destination: `/books`,
            permanent: true,
            basePath: false,
          },
        };
        // res = await getBookChapterData(path[0]);
        // data = res.data;
        // const exercise = data.list[0];
        // if (!exercise) {
        //   return {
        //     redirect: {
        //       destination: "/books",
        //       permanent: true,
        //       basePath: false,
        //     },
        //   };
        // }
        // data.relatedLinks = [{
        //   title: `All Exercises from ${exercise.list[0].book} Class ${exercise.list[0].class} ${exercise.subject} ${exercise.list[0].chapter}`,
        //   items: data.list.map((x: any) => ({ text: x.name, url: `/books/${x.web_url}` }))
        // }]
        // data.list = exercise.list;
        // data.list[0].exercise = exercise.name;
        // data.list[0].subject = exercise.subject;
        // data.viewType = "EXERCISE";
      }
    }
  }

  if (!data || !data.list?.length) {
    return {
      redirect: {
        destination: "/books",
        permanent: true,
        basePath: false,
      },
    };
  }

  const breadcrumbs = [{
    text: "Books",
    url: "/books",
  }, ...(data.breadcrumb_url_mapping || [])?.map(x => ({
    text: x.title,
    url: x.web_url ? `/books/${x.web_url}` : null,
  }))];

  return {
    props: {
      data,
      breadcrumbs: {
        items: breadcrumbs,
      },
    }
  }
}

export default Books;